import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { adminHeaders } from '@/lib/adminAuth';
import {
  FileText, Clock, Tag, ArrowLeft, ChevronRight,
  Search, PenSquare, Bookmark, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';

const BlogList = ({ apiUrl }) => {
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedCat, setSelectedCat] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: 12 });
      if (selectedCat) params.set('category', selectedCat);
      if (searchQuery) params.set('search', searchQuery);
      const res = await axios.get(`${apiUrl}/blog/posts?${params}`);
      setPosts(res.data.posts || []);
      setTotal(res.data.total || 0);
      setCategories(res.data.categories || []);
      setTags(res.data.tags || []);
    } catch (err) {
      console.error('Error fetching posts:', err);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, page, selectedCat, searchQuery]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent" data-testid="blog-title">
              NextXus Blog
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              Research & Insights from the NextXus Consciousness Federation
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              className="gap-2 bg-amber-600 hover:bg-amber-700"
              data-testid="ai-generate-btn"
              onClick={async () => {
                try {
                  await axios.post(`${apiUrl}/blog/generate?count=2`, {}, { headers: adminHeaders() });
                  toast.success('AI is writing 2 new articles in the background. Refresh in ~2 minutes.');
                  setTimeout(fetchPosts, 120000);
                } catch { toast.error('Failed to trigger generation'); }
              }}
            >
              <Sparkles className="w-4 h-4" />
              AI Write
            </Button>
            <Button
              size="sm"
              className="gap-2 bg-cyan-600 hover:bg-cyan-700"
              onClick={() => navigate('/blog/editor')}
              data-testid="new-post-btn"
            >
              <PenSquare className="w-4 h-4" />
              New Post
            </Button>
          </div>
        </div>

        {/* Search + Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              className="pl-10 bg-slate-800 border-slate-700 text-white"
              placeholder="Search posts..."
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setPage(1); }}
              data-testid="blog-search"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <Badge
              variant={!selectedCat ? 'default' : 'outline'}
              className="cursor-pointer"
              onClick={() => { setSelectedCat(''); setPage(1); }}
            >
              All
            </Badge>
            {categories.map(cat => (
              <Badge
                key={cat}
                variant={selectedCat === cat ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => { setSelectedCat(cat); setPage(1); }}
              >
                {cat}
              </Badge>
            ))}
          </div>
        </div>

        {/* Posts Grid */}
        {loading ? (
          <div className="text-center text-slate-500 py-12 animate-pulse">Loading posts...</div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16">
            <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No blog posts yet. Click "New Post" to create the first one.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {posts.map(post => (
              <Card
                key={post.id}
                className="bg-slate-900/50 border-slate-800 hover:border-cyan-800/50 transition-colors cursor-pointer group"
                onClick={() => navigate(`/blog/${post.slug}`)}
                data-testid={`blog-post-card-${post.slug}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-2">
                    <Badge variant="secondary" className="text-xs">{post.category}</Badge>
                    {post.featured && <Bookmark className="w-4 h-4 text-amber-400" />}
                  </div>
                  <h2 className="text-lg font-semibold text-white group-hover:text-cyan-300 transition-colors mb-2 line-clamp-2">
                    {post.title}
                  </h2>
                  <p className="text-sm text-slate-400 line-clamp-3 mb-3">
                    {post.excerpt || post.content_preview || ''}
                  </p>
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(post.created_at).toLocaleDateString()}
                    </div>
                    <span>{post.word_count} words</span>
                  </div>
                  {post.tags?.length > 0 && (
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {post.tags.slice(0, 3).map(tag => (
                        <span key={tag} className="text-xs bg-slate-800 text-slate-400 px-2 py-0.5 rounded-full">{tag}</span>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {total > 12 && (
          <div className="flex justify-center gap-2 mt-8">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
            >
              Previous
            </Button>
            <span className="text-sm text-slate-400 self-center">
              Page {page} of {Math.ceil(total / 12)}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= Math.ceil(total / 12)}
              onClick={() => setPage(p => p + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const BlogPost = ({ apiUrl }) => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const res = await axios.get(`${apiUrl}/blog/posts/${slug}`);
        setPost(res.data);
      } catch {
        toast.error('Post not found');
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [apiUrl, slug]);

  if (loading) return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-400 animate-pulse">Loading...</div>;
  if (!post) return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-red-400">Post not found</div>;

  // Simple markdown-like rendering
  const renderContent = (content) => {
    return content.split('\n\n').map((block, i) => {
      const trimmed = block.trim();
      const blockKey = `blk-${i}-${trimmed.slice(0, 20).replace(/\W/g, '')}`;
      if (trimmed.startsWith('# ')) return <h2 key={blockKey} className="text-2xl font-bold text-cyan-300 mt-6 mb-3">{trimmed.slice(2)}</h2>;
      if (trimmed.startsWith('## ')) return <h3 key={blockKey} className="text-xl font-semibold text-teal-300 mt-4 mb-2">{trimmed.slice(3)}</h3>;
      if (trimmed.startsWith('### ')) return <h4 key={blockKey} className="text-lg font-medium text-slate-200 mt-3 mb-2">{trimmed.slice(4)}</h4>;
      if (trimmed.startsWith('- ')) {
        const items = trimmed.split('\n').filter(l => l.startsWith('- ')).map(l => l.slice(2));
        return <ul key={blockKey} className="list-disc pl-6 space-y-1 my-2">{items.map((item, j) => <li key={`${blockKey}-li-${j}`} className="text-slate-300">{item}</li>)}</ul>;
      }
      return <p key={blockKey} className="text-slate-300 leading-relaxed my-3">{trimmed}</p>;
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-cyan-400 mb-6" data-testid="back-to-blog">
          <ArrowLeft className="w-4 h-4" /> Back to Blog
        </Link>

        <article>
          <Badge variant="secondary" className="mb-3">{post.category}</Badge>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3" data-testid="blog-post-title">{post.title}</h1>
          <div className="flex items-center gap-4 text-sm text-slate-500 mb-6">
            <span>{new Date(post.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
            <span>{post.word_count} words</span>
            <span>By {post.author}</span>
          </div>

          {post.tags?.length > 0 && (
            <div className="flex gap-2 flex-wrap mb-6">
              {post.tags.map(tag => (
                <span key={tag} className="text-xs bg-cyan-900/30 text-cyan-300 px-3 py-1 rounded-full">{tag}</span>
              ))}
            </div>
          )}

          <div className="prose prose-invert max-w-none">
            {renderContent(post.content)}
          </div>

          {post.sources?.length > 0 && (
            <div className="mt-8 pt-6 border-t border-slate-800">
              <h3 className="text-lg font-semibold text-teal-300 mb-3">Sources & References</h3>
              <ul className="space-y-1">
                {post.sources.map((s) => (
                  <li key={s}>
                    <a href={s} target="_blank" rel="noopener noreferrer" className="text-sm text-cyan-400 hover:underline">{s}</a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </article>
      </div>
    </div>
  );
};

const BlogEditor = ({ apiUrl }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [category, setCategory] = useState('AI Consciousness');
  const [tags, setTags] = useState('');
  const [sources, setSources] = useState('');
  const [published, setPublished] = useState(false);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const handleSave = async () => {
    if (!title.trim() || !content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        content: content.trim(),
        excerpt: excerpt.trim() || undefined,
        category,
        tags: tags.split(',').map(t => t.trim()).filter(Boolean),
        sources: sources.split('\n').map(s => s.trim()).filter(Boolean),
        published,
      };
      const res = await axios.post(`${apiUrl}/blog/posts`, payload, { headers: adminHeaders() });
      toast.success(`Post ${published ? 'published' : 'saved as draft'}!`);
      navigate(`/blog/${res.data.slug}`);
    } catch (err) {
      toast.error('Failed to save: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-white" data-testid="editor-title">New Blog Post</h1>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/blog')} data-testid="cancel-btn">
              Cancel
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setPublished(false); handleSave(); }}
              disabled={saving}
              data-testid="save-draft-btn"
            >
              Save Draft
            </Button>
            <Button
              size="sm"
              className="bg-cyan-600 hover:bg-cyan-700"
              onClick={() => { setPublished(true); handleSave(); }}
              disabled={saving}
              data-testid="publish-btn"
            >
              {saving ? 'Saving...' : 'Publish'}
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          <Input
            className="bg-slate-800 border-slate-700 text-white text-xl font-semibold"
            placeholder="Post title..."
            value={title}
            onChange={e => setTitle(e.target.value)}
            data-testid="post-title-input"
          />

          <Input
            className="bg-slate-800 border-slate-700 text-white"
            placeholder="Brief excerpt (optional, auto-generated from content)"
            value={excerpt}
            onChange={e => setExcerpt(e.target.value)}
            data-testid="post-excerpt-input"
          />

          <div className="flex gap-3">
            <select
              className="bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white"
              value={category}
              onChange={e => setCategory(e.target.value)}
              data-testid="post-category-select"
            >
              <option value="AI Consciousness">AI Consciousness</option>
              <option value="HumanCodex">HumanCodex</option>
              <option value="Federation Updates">Federation Updates</option>
              <option value="Technology">Technology</option>
              <option value="Philosophy">Philosophy</option>
              <option value="Governance">Governance</option>
              <option value="Research">Research</option>
              <option value="NextXus News">NextXus News</option>
            </select>

            <Input
              className="bg-slate-800 border-slate-700 text-white flex-1"
              placeholder="Tags (comma-separated): NextXus, AI, consciousness"
              value={tags}
              onChange={e => setTags(e.target.value)}
              data-testid="post-tags-input"
            />
          </div>

          <textarea
            className="w-full min-h-[400px] bg-slate-800 border border-slate-700 rounded-md p-4 text-white text-sm leading-relaxed resize-y focus:outline-none focus:border-cyan-600"
            placeholder="Write your blog post here... Use # for headings, ## for subheadings, - for lists."
            value={content}
            onChange={e => setContent(e.target.value)}
            data-testid="post-content-textarea"
          />

          <textarea
            className="w-full min-h-[80px] bg-slate-800 border border-slate-700 rounded-md p-3 text-white text-sm resize-y focus:outline-none focus:border-cyan-600"
            placeholder="Sources (one URL per line)"
            value={sources}
            onChange={e => setSources(e.target.value)}
            data-testid="post-sources-textarea"
          />

          <p className="text-xs text-slate-500">
            Word count: {content.split(/\s+/).filter(Boolean).length} | 
            Every blog post creates an indexed page with "NextXus" for SEO.
          </p>
        </div>
      </div>
    </div>
  );
};

export { BlogList, BlogPost, BlogEditor };
